import React from 'react';
import { Lock, PenLine } from 'lucide-react';
import { DocumentSectionRendererProps } from './types';
import { cn } from '../../../lib/utils';
import { ViewNode } from '../types';

export const DocumentSectionRenderer: React.FC<
    DocumentSectionRendererProps
> = ({ section, isNested = false, variant = 'card', context, renderNode }) => {
    const { watch, checkHidden, isRequired, contextData } = context;
    const sectionFields = Array.isArray(section.fields) ? section.fields : [];
    if (section.hidden && checkHidden(section.hidden)) {
        return null;
    }
    const getValue = (key: string) => {
        const watchedValue = watch(key);
        if (watchedValue !== undefined) {
            return watchedValue;
        }
        return contextData[key];
    };

    const requirementsStatusField = sectionFields.find(
        (field): field is ViewNode => {
            if (
                field.kind !== 'view' ||
                field.viewType !== 'requirements-status'
            ) {
                return false;
            }
            if (field.hidden && checkHidden(field.hidden)) {
                return false;
            }
            return true;
        }
    );

    const requirementsStatusState = (() => {
        if (!requirementsStatusField) return undefined;
        const dataSource =
            typeof requirementsStatusField.viewProps?.dataSource === 'string'
                ? requirementsStatusField.viewProps.dataSource
                : '';
        if (!dataSource) return undefined;
        const statusPayload = getValue(dataSource);
        const nextState = statusPayload?.state;
        if (
            nextState === 'warning' ||
            nextState === 'success' ||
            nextState === 'neutral'
        ) {
            return nextState;
        }
        return undefined;
    })();

    // Visual link between attestations and the sections they lock —
    // derived from the schema (no schema changes): a shared accent rail and
    // chips naming the counterpart, amber while pending, emerald once signed.
    const lockedByLinks = !isNested
        ? context.attestationLinks?.lockedBy?.[section.id]
        : undefined;
    const signsEntry = !isNested
        ? context.attestationLinks?.signs?.[section.id]
        : undefined;
    const lockedBySigned = (lockedByLinks || []).some(
        (link) => getValue(link.attestationId) === true
    );
    const signsSigned = (signsEntry?.attestationIds || []).some(
        (attestationId) => getValue(attestationId) === true
    );
    const linkChip = (() => {
        if (isNested) return null;
        if (lockedByLinks?.length) {
            return (
                <span
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                        lockedBySigned
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                            : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                    )}
                >
                    {lockedBySigned ? (
                        <Lock className="h-3 w-3" />
                    ) : (
                        <PenLine className="h-3 w-3" />
                    )}
                    {lockedBySigned
                        ? 'Locked — signed below'
                        : 'Locks when signed below'}
                </span>
            );
        }
        if (signsEntry?.targets.length) {
            return (
                <span
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                        signsSigned
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                            : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                    )}
                >
                    {signsSigned ? (
                        <Lock className="h-3 w-3" />
                    ) : (
                        <PenLine className="h-3 w-3" />
                    )}
                    Signs: {signsEntry.targets
                        .map((target) => target.sectionTitle)
                        .join(', ')}
                </span>
            );
        }
        return null;
    })();

    const backgroundComponentKey =
        section.background?.functionKey || section.background?.component;
    const BackgroundComponent = backgroundComponentKey
        ? context.functions.backgrounds[backgroundComponentKey]
        : undefined;

    const showBackground =
        !section.style?.noBackground &&
        section.background &&
        section.background.condition &&
        watch(section.background.condition);

    const shouldApplyStateTone =
        !section.style?.noBackground &&
        (requirementsStatusState === 'warning' ||
            requirementsStatusState === 'success');
    const sectionBorderClass = section.style?.noBorder
        ? 'border border-transparent'
        : shouldApplyStateTone
        ? requirementsStatusState === 'warning'
            ? 'border-amber-400/40'
            : 'border-emerald-400/35'
        : 'border border-border/45';
    const sectionBackgroundClass = section.style?.noBackground
        ? 'bg-transparent'
        : shouldApplyStateTone
        ? requirementsStatusState === 'warning'
            ? 'bg-amber-500/[0.08]'
            : 'bg-emerald-500/[0.08]'
        : showBackground
        ? section.background?.style || 'bg-card/55'
        : 'bg-card/55';
    const sectionTextToneClass = shouldApplyStateTone
        ? 'text-foreground'
        : 'text-foreground';
    const descriptionToneClass = shouldApplyStateTone
        ? requirementsStatusState === 'warning'
            ? 'text-amber-800 dark:text-amber-100/80'
            : 'text-emerald-800 dark:text-emerald-100/80'
        : 'text-muted-foreground/90';

    const containerClasses = isNested
        ? cn(
              'space-y-4 pt-4',
              section.style?.noBorder ? '' : 'border-t border-border/35'
          )
        : variant === 'grouped'
        ? cn(
              // Encapsulated inside a group wrapper card: keep tinting but
              // no own border/rounding — the wrapper provides the chrome.
              'relative overflow-hidden p-6 transition-colors duration-200',
              sectionBackgroundClass,
              sectionTextToneClass,
              section.style?.className
          )
        : cn(
              'relative overflow-hidden rounded-xl p-6 shadow-sm transition-colors duration-200',
              sectionBorderClass,
              sectionBackgroundClass,
              sectionTextToneClass,
              section.style?.className
          );

    return (
        <div key={section.id} className={containerClasses}>
            {!isNested && showBackground && BackgroundComponent && (
                <BackgroundComponent />
            )}

            <div className="relative z-10">
                {!section.style?.hideTitle && (
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h2
                                className={
                                    isNested
                                        ? 'text-lg font-semibold'
                                        : 'text-xl font-semibold tracking-tight'
                                }
                            >
                                {section.title}
                            </h2>
                            {linkChip}
                        </div>
                        {section.description && (
                            <p className={cn('text-sm', descriptionToneClass)}>
                                {section.description}
                            </p>
                        )}
                    </div>
                )}

                <div
                    className={cn(
                        'flex flex-wrap -mx-2',
                        section.style?.hideTitle ? '' : 'pt-4'
                    )}
                >
                    {sectionFields.map((field, index) => {
                        const position = field.style?.layout?.position;
                        const isFirstRight =
                            position === 'right' &&
                            !sectionFields
                                .slice(0, index)
                                .some(
                                    (f) => f.style?.layout?.position === 'right'
                                );
                        const widthStyle: React.CSSProperties = {
                            width: field.style?.layout?.width || '100%',
                            ...(isFirstRight && { marginLeft: 'auto' }),
                        };

                        if (
                            field.kind !== 'section' &&
                            field.hidden &&
                            checkHidden(field.hidden)
                        ) {
                            return null;
                        }

                        return (
                            <div
                                key={field.id}
                                // max-sm:!w-full beats the inline width so
                                // 50%-wide schema fields stack on phones.
                                className="px-2 mb-4 max-sm:!w-full"
                                style={widthStyle}
                            >
                                <div className="space-y-2">
                                    {field.kind === 'input' &&
                                        !field.style?.hideLabel && (
                                            <label className="text-sm font-medium leading-none text-foreground/95 peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                                {field.title}
                                                {isRequired(field) && (
                                                    <span className="ml-1 text-destructive">
                                                        *
                                                    </span>
                                                )}
                                            </label>
                                        )}
                                    {field.kind === 'action' &&
                                        field.actionType === 'toggle-tags' && (
                                            <label className="mb-2 block text-sm font-medium leading-none text-foreground/95 peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                                {field.title}
                                                {isRequired(field) && (
                                                    <span className="ml-1 text-destructive">
                                                        *
                                                    </span>
                                                )}
                                            </label>
                                        )}
                                    {renderNode(field)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
